/* ==========================================================================
   MDI scan booking
   --------------------------------------------------------------------------
   Talks to MdiBookingApi in Salesforce:

     GET  {endpoint}/config                        publishable key, hold length
     GET  {endpoint}/services                      the catalogue
     GET  {endpoint}/availability?date=&service=   open start times
     POST {endpoint}/hold                          reserve a slot, capture the lead
     POST {endpoint}/pay                           charge and confirm

   NOTHING HERE DECIDES ANYTHING THAT MATTERS
   Price, duration, whether a slot is free and whether a hold is still live are
   all decided in Salesforce, on every request. This file renders what it is told
   and posts what was chosen. Anything a browser computes is something a browser
   can be made to lie about.

   ONE PRIMARY ACTION
   Every step is advanced by the same button in the summary, whose label says
   what will happen. Choosing a service does not jump the page — a choice you can
   see and change before committing is calmer than one that moves under you, and
   it is what Square's booking flow does.

   ES5 on purpose, matching ta-leadform.js.
   ========================================================================== */

(function () {
  'use strict';

  var STRIPE_JS = 'https://js.stripe.com/v3/';
  var DAYS_AHEAD = 21;
  var TIMEOUT = 20000;
  var ORDER = ['service', 'details', 'time', 'pay'];

  function boot(root) {
    var endpoint = (root.dataset.endpoint || '').replace(/\/+$/, '');
    if (!endpoint) return;

    var s = {
      step: 'service',
      service: null,
      details: null,
      day: null,
      slot: null,
      slotLabel: null,
      bookingId: null,
      amount: null,
      holdMinutes: 10,
      publishableKey: null,
      connectedAccountId: null,
      stripe: null,
      card: null,
      timer: null,
      busy: false,
      /* Set the first time a person types or clicks inside the details form. Autofill does not
         produce these events, so it distinguishes a filled honeypot from a used one. */
      humanTouchedForm: false
    };

    var el = {
      steps: root.querySelectorAll('[data-step]'),
      services: root.querySelector('[data-services]'),
      serviceError: root.querySelector('[data-service-error]'),
      detailsForm: root.querySelector('[data-details-form]'),
      detailsError: root.querySelector('[data-details-error]'),
      days: root.querySelector('[data-days]'),
      slots: root.querySelector('[data-slots]'),
      timeError: root.querySelector('[data-time-error]'),
      timeSub: root.querySelector('[data-time-sub]'),
      payForm: root.querySelector('[data-pay-form]'),
      payError: root.querySelector('[data-pay-error]'),
      cardMount: root.querySelector('[data-card-element]'),
      primary: root.querySelector('[data-primary]'),
      back: root.querySelector('[data-back]'),
      hold: root.querySelector('[data-hold]'),
      sumService: root.querySelector('[data-sum-service]'),
      sumWhen: root.querySelector('[data-sum-when]'),
      sumWhere: root.querySelector('[data-sum-where]'),
      sumTotal: root.querySelector('[data-sum-total]'),
      barTotal: root.querySelector('[data-bar-total]'),
      aside: root.querySelector('[data-aside]'),
      frame: root.querySelector('[data-frame]'),
      receipt: root.querySelector('[data-receipt]'),
      doneSub: root.querySelector('[data-done-sub]')
    };

    /* ---------- phone ----------
       Capped at ten digits and formatted as it is typed. The validator already
       demands exactly ten and the submit strips to digits, so this changes nothing
       about what is sent — it stops somebody typing fifteen and only finding out
       when they press Continue, and it makes a number that is being read aloud to
       a technician legible. */
    var phoneInput = root.querySelector('#mb-phone');
    if (phoneInput) {
      phoneInput.setAttribute('maxlength', '14');   // (714) 555-0000
      phoneInput.addEventListener('input', function () {
        var caretAtEnd = phoneInput.selectionStart === phoneInput.value.length;
        var d = phoneInput.value.replace(/\D/g, '').slice(0, 10);
        var out = d;
        if (d.length > 6)      out = '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
        else if (d.length > 3) out = '(' + d.slice(0, 3) + ') ' + d.slice(3);
        else if (d.length > 0) out = '(' + d;
        if (out !== phoneInput.value) {
          phoneInput.value = out;
          /* Only chase the caret to the end when it was already there. Rewriting it
             mid-string would fight somebody correcting a digit in the middle. */
          if (caretAtEnd) phoneInput.setSelectionRange(out.length, out.length);
        }
      });
    }

    /* ---------- details carried in from the lead form ----------
       The TruAligner lead form on the Salesforce site hands people here once they decide they
       would rather the scanner came to them, and it puts what they have already typed in the
       query string. Filling those boxes IS the hand-off: somebody who just gave their name,
       email, phone and ZIP two screens ago will not type them again.

       Only the fields this form owns are read, matched on the input's own `name`, so a stray
       or renamed parameter fills nothing rather than filling something wrong.

       Nothing is dispatched and nothing is validated. The rules run on blur and again in
       readDetails() when Continue is pressed, so a value simply sitting in its box is read
       correctly whenever it is next looked at. It does not touch the honeypot either: the
       hidden company_url is not in the list, and s.humanTouchedForm is set by keydown and
       pointerdown, neither of which this causes. */
    (function prefillFromQuery() {
      var carried;
      try {
        carried = new URLSearchParams(window.location.search);
      } catch (e) {
        return;   /* A URL we cannot read is the same as one with nothing in it. */
      }
      if (!carried.toString()) return;

      /* street/city/state joined the list once the lead form started asking for them. They
         are optional there, so any of the three may simply be absent - the loop skips a
         blank and the booking form asks for it in the ordinary way. */
      var CARRIED = ['firstName', 'lastName', 'email', 'phone', 'postalCode',
                     'street', 'city', 'state'];
      for (var i = 0; i < CARRIED.length; i++) {
        var value = (carried.get(CARRIED[i]) || '').trim();
        if (!value) continue;
        var input = el.detailsForm.querySelector('input[name="' + CARRIED[i] + '"]');
        if (input) input.value = value;
      }

      /* Then take it back out of the address bar. That query string is a named person's email
         and phone number, and left there it goes into browser history, the referrer sent with
         every asset this page loads, and any analytics that records a URL. Stripping it once
         the boxes are filled costs nothing - the values are already where they are needed. */
      try {
        window.history.replaceState({}, document.title,
          window.location.pathname + window.location.hash);
      } catch (e) { /* an old browser keeps its query string; the form still works */ }
    })();

    /* ---------- plumbing ---------- */

    function api(path, options) {
      var stop = new AbortController();
      var timer = setTimeout(function () { stop.abort(); }, TIMEOUT);
      var opts = options || {};
      opts.signal = stop.signal;
      return fetch(endpoint + path, opts)
        .then(function (r) { return r.text().then(function (t) { return { r: r, t: t }; }); })
        .then(function (res) {
          clearTimeout(timer);
          var body;
          try { body = JSON.parse(res.t); } catch (e) { body = null; }
          /* A refusal from Salesforce carries wording written for the patient
             ("that time has just been taken"), so it is shown rather than
             replaced with something generic. */
          if (!body) throw new Error('We could not reach the booking system. Please try again.');
          if (body.success !== true) {
            var err = new Error(body.message || 'That did not work. Please try again.');
            err.declined = body.declined === true;
            throw err;
          }
          return body;
        });
    }

    function post(path, payload) {
      return api(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    function money(n) { return '$' + Number(n).toFixed(2).replace(/\.00$/, ''); }

    function fail(node, message) {
      if (!node) return;
      node.textContent = message;
      node.hidden = false;
    }
    function clearFail(node) { if (node) { node.hidden = true; node.textContent = ''; } }

    /* ---------- step + summary ---------- */

    function go(name) {
      s.step = name;
      var panels = root.querySelectorAll('[data-panel]');
      for (var i = 0; i < panels.length; i++) {
        panels[i].hidden = panels[i].dataset.panel !== name;
      }
      var at = ORDER.indexOf(name);
      for (var j = 0; j < el.steps.length; j++) {
        var b = el.steps[j];
        var pos = ORDER.indexOf(b.dataset.step);
        b.classList.toggle('is-on', pos === at);
        b.classList.toggle('is-done', at > -1 && pos < at);
      }
      /* The connector BEFORE a reached step is drawn as completed, so the bar fills
         left to right rather than only the circles changing. */
      var seps = el.steps[0] ? el.steps[0].closest('[data-steps]').querySelectorAll('.mb__sep') : [];
      for (var k = 0; k < seps.length; k++) {
        seps[k].classList.toggle('is-done', at > -1 && k < at);
      }

      el.back.hidden = !(at > 0);
      if (name === 'done') {
        el.aside.hidden = true;
        /* Drop the second column with it, or the receipt sits against the left edge
           with a third of the page empty beside it. */
        if (el.frame) el.frame.classList.add('is-done');
      }

      /* Stepping back to the scan or the address DROPS any time already chosen,
         and stops the countdown with it.

         Both of those choices decide what a valid time even is: the service sets
         the length of the visit, and the address sets which territory serves it.
         Leaving a held 3:00 PM on screen while someone edits the address behind it
         shows them a reservation that may no longer be theirs to take — and if
         they then pick a different slot, the first hold lingers until it expires.
         Better to make the loss explicit here than to let a stale time follow them
         to the payment screen. The abandoned hold in Salesforce lapses on its own
         and the sweeper tidies it. */
      if (name === 'service' || name === 'details') {
        s.slot = null;
        s.slotLabel = null;
        s.bookingId = null;
        s.amount = null;
        clearInterval(s.timer);
        el.hold.hidden = true;
      }

      /* The countdown belongs to the payment screen. Anywhere else it is a
         number ticking down beside something it does not describe. */
      if (name !== 'pay') el.hold.hidden = true;

      /* Focus the heading of the step that just opened, or a keyboard user is
         left on a control that no longer exists. */
      var h = root.querySelector('[data-panel="' + name + '"] h2');
      if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }

      paint();
    }

    /** The summary and the primary action are derived from state, in one place,
     *  so a label and what it does can never disagree. */
    function paint() {
      el.sumService.textContent = s.service ? s.service.name : 'Not chosen';
      el.sumService.classList.toggle('is-empty', !s.service);

      el.sumWhen.textContent = s.slot ? whenText() : 'Not chosen';
      el.sumWhen.classList.toggle('is-empty', !s.slot);

      el.sumWhere.textContent = s.details
        ? s.details.street + ', ' + s.details.city + ', ' + s.details.state
        : 'Not entered';
      el.sumWhere.classList.toggle('is-empty', !s.details);

      var total = s.service ? money(s.service.price) : '—';
      el.sumTotal.textContent = total;
      el.barTotal.textContent = s.service ? total : '';

      var label = 'Continue';
      var ready = false;
      if (s.step === 'service') { label = 'Continue'; ready = !!s.service; }
      else if (s.step === 'details') { label = 'See available times'; ready = true; }
      else if (s.step === 'time') { label = 'Continue'; ready = !!s.slot; }
      else if (s.step === 'pay') {
        label = s.amount != null ? 'Pay ' + money(s.amount) : 'Pay';
        ready = true;
      }
      if (!s.busy) el.primary.textContent = label;
      el.primary.disabled = s.busy || !ready;
    }

    function whenText() {
      var d = new Date(s.slot);
      return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
        + ', ' + s.slotLabel;
    }

    function busy(on, label) {
      s.busy = on;
      if (on) {
        el.primary.innerHTML = '';
        var sp = document.createElement('span');
        sp.className = 'mb__spin';
        el.primary.appendChild(sp);
        el.primary.appendChild(document.createTextNode(label || 'Working…'));
        el.primary.disabled = true;
      } else {
        paint();
      }
    }

    /* ---------- 1. services ---------- */

    /* The catalogue is a round trip to Salesforce, and until it answered this panel was
       simply blank - the first screen of the booking flow showed a heading, a subheading
       and nothing at all, which reads as a page that has finished loading and has nothing
       to offer. Especially bad for anyone arriving from the lead form, who has just been
       promised they can book a scan.

       A shape rather than the word "Loading": .mb__skel/.mb__skel-row already existed in
       the stylesheet for exactly this and had never been wired to anything. One row,
       because the catalogue holds one active service - the point of a skeleton is that
       nothing moves when the real thing arrives, and two placeholders for one card would
       make it jump on purpose. */
    function skeletonServices() {
      var wrap = document.createElement('div');
      wrap.className = 'mb__skel';
      wrap.setAttribute('aria-hidden', 'true');
      var row = document.createElement('div');
      row.className = 'mb__skel-row';
      wrap.appendChild(row);
      return wrap;
    }

    function loadServices() {
      clearFail(el.serviceError);
      el.services.innerHTML = '';
      el.services.appendChild(skeletonServices());
      /* Announced as busy, so a screen reader is told to wait rather than reading out an
         empty region and moving on. renderServices and the catch both clear it. */
      el.services.setAttribute('aria-busy', 'true');

      api('/services')
        .then(function (body) { renderServices(body.services || []); })
        .catch(function (e) {
          el.services.innerHTML = '';
          el.services.removeAttribute('aria-busy');
          fail(el.serviceError, e.message);
        });
    }

    function renderServices(list) {
      el.services.innerHTML = '';
      el.services.removeAttribute('aria-busy');
      if (!list.length) {
        el.services.innerHTML =
          '<p class="mb__empty">No scans are bookable online right now.</p>';
        return;
      }
      list.forEach(function (svc) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'mb__opt';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', 'false');
        b.innerHTML =
          '<span class="mb__radio" aria-hidden="true"></span>' +
          '<span class="mb__opt-name"></span>' +
          '<span class="mb__opt-meta"></span>' +
          '<span class="mb__opt-desc"></span>';
        b.querySelector('.mb__opt-name').textContent = svc.name;
        b.querySelector('.mb__opt-meta').innerHTML =
          '<span class="mb__opt-mins">' + svc.durationMinutes + ' min</span>&nbsp;&nbsp;'
          + money(svc.price);
        b.querySelector('.mb__opt-desc').textContent = svc.description || '';
        b.addEventListener('click', function () {
          var all = el.services.querySelectorAll('[role="radio"]');
          for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-checked', 'false');
          b.setAttribute('aria-checked', 'true');
          s.service = svc;
          /* A picked service invalidates a time picked for a different length. */
          s.slot = null; s.slotLabel = null;
          paint();
        });
        el.services.appendChild(b);
      });
    }

    /* ---------- 2. details ---------- */

    /* One rule per field, each saying what is wrong and what to do — never
       "invalid input". The rules are deliberately forgiving: a phone is checked
       for ten digits after punctuation is stripped, because people type numbers
       every which way and rejecting a real number is worse than accepting a
       formatted one. Nothing here is a security check; Salesforce validates
       again and is the only opinion that counts. */
    var RULES = {
      firstName: function (v) { return v ? null : 'Enter your first name.'; },
      lastName:  function (v) { return v ? null : 'Enter your last name.'; },
      email: function (v) {
        if (!v) return 'Enter your email address.';
        return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)
          ? null : 'That does not look like an email address.';
      },
      phone: function (v) {
        if (!v) return 'Enter a phone number.';
        var digits = v.replace(/\D/g, '');
        if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
        return digits.length === 10 ? null : 'Enter a 10-digit phone number.';
      },
      street: function (v) { return v ? null : 'Enter the street address for the visit.'; },
      city:   function (v) { return v ? null : 'Enter the city.'; },
      state: function (v) {
        if (!v) return 'Enter the state.';
        return /^[A-Za-z]{2}$/.test(v) ? null : 'Use the two-letter state code, like CA.';
      },
      postalCode: function (v) {
        if (!v) return 'Enter the ZIP code.';
        return /^\d{5}$/.test(v) ? null : 'A ZIP code is five digits.';
      }
    };

    function fieldOf(input) { return input.closest('.mb__field'); }

    /** Check one field and show or clear its message. Returns true when valid. */
    function checkField(input) {
      var rule = RULES[input.name];
      if (!rule) return true;
      var value = input.value.trim();
      var problem = rule(value);
      var field = fieldOf(input);
      var msg = field ? field.querySelector('.mb__msg') : null;

      if (problem) {
        input.setAttribute('aria-invalid', 'true');
        if (field) { field.classList.add('is-bad'); field.classList.remove('is-ok'); }
        if (msg) { msg.textContent = problem; msg.hidden = false; }
        return false;
      }
      input.removeAttribute('aria-invalid');
      if (field) {
        field.classList.remove('is-bad');
        field.classList.toggle('is-ok', value.length > 0);
      }
      if (msg) { msg.hidden = true; msg.textContent = ''; }
      return true;
    }

    /* Checked when a field is left, not on every keystroke: telling someone their
       email is wrong while they are still typing it is nagging, not helping. Once
       a field HAS been marked wrong it re-checks as they type, so the error
       clears the moment they fix it. */
    (function wireValidation() {
      var inputs = el.detailsForm.querySelectorAll('input[name]');
      for (var i = 0; i < inputs.length; i++) {
        (function (input) {
          if (!RULES[input.name]) return;
          input.addEventListener('blur', function () { checkField(input); });
          input.addEventListener('input', function () {
            var field = fieldOf(input);
            if (field && field.classList.contains('is-bad')) checkField(input);
          });
        })(inputs[i]);
      }
    })();

    function readDetails() {
      var data = {};
      var firstBad = null;
      var inputs = el.detailsForm.querySelectorAll('input[name]');
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        data[input.name] = input.value.trim();
        if (RULES[input.name] && !checkField(input) && !firstBad) firstBad = input;
      }
      if (firstBad) {
        /* Focus the first problem rather than listing them all at the bottom —
           the person needs to be taken to the thing to fix, not told a count. */
        firstBad.focus();
        firstBad.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return null;
      }
      /* Normalised on the way out so Salesforce stores one shape, whatever was
         typed: the state code uppercase, the phone as digits only. */
      data.state = data.state.toUpperCase();
      data.phone = data.phone.replace(/\D/g, '');
      return data;
    }

    /* ---------- 3. time ---------- */

    function isoDay(d) {
      return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0');
    }

    function renderDays() {
      el.days.innerHTML = '';
      var today = new Date();
      for (var i = 0; i < DAYS_AHEAD; i++) {
        var d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'mb__day';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', 'false');
        b.dataset.day = isoDay(d);
        b.innerHTML = '<span class="mb__day-dow"></span><span class="mb__day-num"></span>';
        b.querySelector('.mb__day-dow').textContent =
          i === 0 ? 'Today' : d.toLocaleDateString(undefined, { weekday: 'short' });
        b.querySelector('.mb__day-num').textContent = d.getDate();
        b.addEventListener('click', function (ev) { selectDay(ev.currentTarget.dataset.day); });
        el.days.appendChild(b);
      }
      selectDay(isoDay(today));
    }

    function selectDay(key) {
      s.day = key;
      s.slot = null; s.slotLabel = null;
      var all = el.days.querySelectorAll('[data-day]');
      for (var i = 0; i < all.length; i++) {
        all[i].setAttribute('aria-selected', String(all[i].dataset.day === key));
      }
      paint();
      loadSlots(key);
    }

    function skeletonSlots() {
      var wrap = document.createElement('div');
      wrap.className = 'mb__skel-slots';
      wrap.setAttribute('aria-hidden', 'true');
      for (var i = 0; i < 12; i++) {
        var d = document.createElement('div');
        d.className = 'mb__skel-slot';
        wrap.appendChild(d);
      }
      return wrap;
    }

    function loadSlots(key) {
      clearFail(el.timeError);
      el.slots.innerHTML = '';
      el.slots.appendChild(skeletonSlots());
      api('/availability?date=' + encodeURIComponent(key) +
          '&service=' + encodeURIComponent(s.service.key))
        .then(function (body) { renderSlots(body.slots || []); })
        .catch(function (e) { el.slots.innerHTML = ''; fail(el.timeError, e.message); });
    }

    /** 24-hour hour from a label like "7:15 PM". Falls back to the browser clock
     *  only if the label is not in the shape Apex sends. */
    function hourFromLabel(label) {
      var m = /^(\d{1,2}):(\d{2})\s*([AaPp])/.exec(label || '');
      if (!m) return 12;
      var h = parseInt(m[1], 10) % 12;
      return (m[3] === 'P' || m[3] === 'p') ? h + 12 : h;
    }

    function renderSlots(slots) {
      el.slots.innerHTML = '';
      if (!slots.length) {
        el.slots.innerHTML =
          '<p class="mb__empty">Nothing open on this day. Try another one.</p>';
        return;
      }
      /* Morning / afternoon / evening: people arrive with a rough time of day in
         mind, and the grouping turns forty buttons into three short choices.

         GROUPED BY THE LABEL, NOT BY new Date().getHours().
         The label is formatted by Apex in the ORG's timezone; getHours() reads
         the BROWSER's. For any visitor outside the clinic's zone the two
         disagree, which is how 7:00 PM ended up filed under Morning. The label
         is what the patient reads and what the clinic means, so it is what
         decides the heading. */
      var groups = [
        { label: 'Morning', from: 0, to: 12, slots: [] },
        { label: 'Afternoon', from: 12, to: 17, slots: [] },
        { label: 'Evening', from: 17, to: 24, slots: [] }
      ];
      slots.forEach(function (slot) {
        var hour = hourFromLabel(slot.label);
        for (var i = 0; i < groups.length; i++) {
          if (hour >= groups[i].from && hour < groups[i].to) { groups[i].slots.push(slot); break; }
        }
      });

      groups.forEach(function (g) {
        if (!g.slots.length) return;
        var wrap = document.createElement('div');
        wrap.className = 'mb__group';
        var h = document.createElement('p');
        h.className = 'mb__group-h';
        h.textContent = g.label;
        var grid = document.createElement('div');
        grid.className = 'mb__slots';
        g.slots.forEach(function (slot) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'mb__slot';
          b.setAttribute('aria-pressed', 'false');
          b.textContent = slot.label;
          b.addEventListener('click', function () {
            var all = el.slots.querySelectorAll('.mb__slot');
            for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', 'false');
            b.setAttribute('aria-pressed', 'true');
            s.slot = slot.startTime;
            s.slotLabel = slot.label;
            paint();
          });
          grid.appendChild(b);
        });
        wrap.appendChild(h);
        wrap.appendChild(grid);
        el.slots.appendChild(wrap);
      });
    }

    /* ---------- hold ---------- */

    function holdSlot() {
      var payload = {
        firstName: s.details.firstName,
        lastName: s.details.lastName,
        email: s.details.email,
        phone: s.details.phone,
        street: s.details.street,
        city: s.details.city,
        state: s.details.state,
        postalCode: s.details.postalCode,
        service: s.service.key,
        startTime: s.slot
      };
      /* The honeypot is sent only when something filled it AND nobody ever focused it.
         A bot fills it without focusing; a browser autofilling the field also never focuses it,
         which is why the flag is cleared on any real interaction with the form instead. Sending it
         on autofill alone turned genuine bookings into silent no-ops. */
      if (s.details.company_url && !s.humanTouchedForm) {
        payload.company_url = s.details.company_url;
      }

      busy(true, 'Holding your time…');
      return post('/hold', payload)
        .then(function (body) {
          /* The API answers 200 with NO bookingId when it thinks the caller is a bot — the
             honeypot path deliberately looks like success so a bot learns nothing. A real visitor
             can land there too if the browser autofills the hidden field, and without this guard
             the page walked on to Payment holding no booking and failed at the last step with
             "A booking and a payment method are required." Refuse here, where it can still be
             explained. */
          if (!body.bookingId) {
            throw new Error('We could not hold that time. Please reload the page and try again.');
          }
          s.bookingId = body.bookingId;
          s.amount = body.amount;
          busy(false);
          go('pay');
          startCountdown();
          return mountCard();
        })
        .catch(function (e) {
          busy(false);
          /* A taken slot answers with its own wording. Refresh the grid so the
             gone time disappears rather than inviting a second attempt at the
             same failure. */
          fail(el.timeError, e.message);
          s.slot = null; s.slotLabel = null;
          loadSlots(s.day);
          paint();
        });
    }

    function startCountdown() {
      var endsAt = Date.now() + s.holdMinutes * 60000;
      clearInterval(s.timer);
      el.hold.hidden = false;
      s.timer = setInterval(tick, 1000);
      tick();

      function tick() {
        var left = Math.max(0, Math.round((endsAt - Date.now()) / 1000));
        var m = Math.floor(left / 60);
        var ss = String(left % 60).padStart(2, '0');
        el.hold.textContent = left > 0
          ? 'Held for you for ' + m + ':' + ss
          : 'Your hold has expired. Please choose a time again.';
        el.hold.classList.toggle('is-urgent', left > 0 && left <= 60);
        if (left <= 0) { clearInterval(s.timer); el.primary.disabled = true; }
      }
    }

    /* ---------- Stripe ---------- */

    function loadStripeJs() {
      if (window.Stripe) return Promise.resolve();
      return new Promise(function (resolve, reject) {
        var sc = document.createElement('script');
        sc.src = STRIPE_JS;
        sc.onload = resolve;
        sc.onerror = function () { reject(new Error('The card form could not load.')); };
        document.head.appendChild(sc);
      });
    }

    function mountCard() {
      if (s.card) return Promise.resolve();
      return loadStripeJs().then(function () {
        if (!s.publishableKey) throw new Error('Card payments are not configured.');
        /* stripeAccount matters, and its absence is invisible until the charge fails.
           MDI is a Stripe Connect connected account reached with the platform's key plus a
           Stripe-Account header, so the card has to be created ON that account for the charge to
           find it. Passing the option here is what puts it there. Omit it and the card lands on
           the platform instead, and the payment fails at the last step with nothing on screen to
           explain why. */
        s.stripe = s.connectedAccountId
          ? window.Stripe(s.publishableKey, { stripeAccount: s.connectedAccountId })
          : window.Stripe(s.publishableKey);
        s.card = s.stripe.elements().create('card', {
          style: {
            base: {
              fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
              fontSize: '16px',
              color: '#094a5d',
              '::placeholder': { color: '#9aacb6' }
            }
          }
        });
        s.card.mount(el.cardMount);
      }).catch(function (e) { fail(el.payError, e.message); });
    }

    function pay() {
      clearFail(el.payError);
      if (!s.stripe || !s.card) {
        fail(el.payError, 'The card form is still loading. Try again in a moment.');
        return;
      }
      busy(true, 'Taking payment…');
      s.stripe.createPaymentMethod({ type: 'card', card: s.card })
        .then(function (result) {
          if (result.error) throw new Error(result.error.message);
          return post('/pay', {
            bookingId: s.bookingId,
            paymentMethodId: result.paymentMethod.id
          });
        })
        .then(function () {
          clearInterval(s.timer);
          busy(false);
          renderDone();
          go('done');
        })
        .catch(function (err) {
          busy(false);
          /* Stays on this screen with the hold still running: a decline usually
             means a different card, not a different appointment. */
          fail(el.payError, err.message);
        });
    }

    function renderDone() {
      el.doneSub.textContent =
        'We’ve emailed a confirmation to ' + s.details.email + '.';
      el.receipt.innerHTML = '';
      [
        ['Scan', s.service.name],
        ['When', whenText()],
        ['Where', s.details.street + ', ' + s.details.city + ', '
          + s.details.state + ' ' + s.details.postalCode],
        ['Paid', money(s.amount)]
      ].forEach(function (pair) {
        var d = document.createElement('div');
        var dt = document.createElement('dt');
        dt.textContent = pair[0];
        var dd = document.createElement('dd');
        dd.textContent = pair[1];
        d.appendChild(dt); d.appendChild(dd);
        el.receipt.appendChild(d);
      });
    }

    /* ---------- one primary action ---------- */

    el.primary.addEventListener('click', function () {
      if (s.busy) return;
      if (s.step === 'service') {
        if (!s.service) return;
        go('details');
      } else if (s.step === 'details') {
        var data = readDetails();
        if (!data) return;
        s.details = data;
        go('time');
        el.timeSub.textContent =
          s.service.durationMinutes + '-minute visit at ' + data.street + ', ' + data.city + '.';
        renderDays();
      } else if (s.step === 'time') {
        if (!s.slot) return;
        holdSlot();
      } else if (s.step === 'pay') {
        pay();
      }
    });

    el.back.addEventListener('click', function () {
      var at = ORDER.indexOf(s.step);
      if (at > 0) go(ORDER[at - 1]);
    });

    /* A completed step is clickable — correcting a typo should not mean starting
       again. Steps ahead of the current one are not, because their inputs do not
       exist yet. */
    for (var i = 0; i < el.steps.length; i++) {
      el.steps[i].addEventListener('click', function (ev) {
        var target = ev.currentTarget.dataset.step;
        if (s.step === 'done') return;
        if (ORDER.indexOf(target) < ORDER.indexOf(s.step)) go(target);
      });
    }

    el.detailsForm.addEventListener('submit', function (ev) { ev.preventDefault(); });
    ['keydown', 'pointerdown'].forEach(function (evt) {
      el.detailsForm.addEventListener(evt, function () { s.humanTouchedForm = true; }, true);
    });
    el.payForm.addEventListener('submit', function (ev) { ev.preventDefault(); });

    /* ---------- start ---------- */

    api('/config')
      .then(function (body) {
        s.publishableKey = body.publishableKey;
        s.connectedAccountId = body.connectedAccountId || null;
        if (body.holdMinutes) s.holdMinutes = Number(body.holdMinutes);
      })
      .catch(function () {
        /* Only the card step needs this, three screens away. Failing here would
           hide a working booking form over something not yet visible. */
      });

    loadServices();
    paint();
  }

  document.addEventListener('DOMContentLoaded', function () {
    var roots = document.querySelectorAll('[data-ta-booking]');
    for (var i = 0; i < roots.length; i++) boot(roots[i]);
  });
})();
